import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { FolderOpen, Images } from 'lucide-react';
import { ChampionshipMediaTab } from 'modules/championship/domain/enums/championship-media-tab';
import { ChampionshipMediaKindRow, ChampionshipMediaSettings } from 'modules/championship/domain/graphql/championship-media';
import {
  useAssignChampionshipTemplateGroupMutation,
  useUnassignChampionshipTemplateGroupMutation,
} from 'modules/championship/store/championship-media-api';
import ChampionshipMediaTemplatesSkeleton, {
  ChampionshipMediaTemplatesSkeletonVariant,
} from 'modules/championship/ui/components/form-parts/media/championship-media-templates-skeleton';
import ChangeTemplateGroupModal from 'modules/championship/ui/components/form-parts/media/change-template-group-modal';
import GenerateMediaModal from 'modules/championship/ui/components/form-parts/media/generate-media-modal';
import MediaKindStatus, { mediaKindState } from 'modules/championship/ui/components/form-parts/media/media-kind-status';
import {
  MEDIA_ASSIGNED_CARD_CLASS,
  MEDIA_ASSIGNED_ICON_CLASS,
  MEDIA_KIND_ACTION_CLASS,
  MEDIA_ROW_LIST_CLASS,
  MEDIA_SECTION_CLASS,
  MEDIA_TEMPLATES_COLUMN_CLASS,
  MEDIA_TEMPLATES_COPY,
} from 'modules/championship/ui/components/form-parts/media/media-tab-layout';
import MediaTabStateCard from 'modules/championship/ui/components/form-parts/media/media-tab-state-card';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { MediaSubjectType, subjectTypeFor } from 'modules/manager/domain/media-kind/media-kind-catalog';
import { useChampionshipMediaSubjectsQuery, useGenerateMediaMutation } from 'modules/manager/store/media-generation-jobs-api';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import UDButton from 'ud-ui/components/button';
import { UDEntityRow } from 'ud-ui/components/paged-list';
import UDText from 'ud-ui/components/text';

interface ChampionshipMediaTemplatesProps {
  championshipId: number;
  /**
   * Организация, которой принадлежит соревнование (`championship.tournamentId` бэкенда).
   * Наборы берутся по ней, а не по активной организации в шапке: супер-админ открывает
   * турниры чужих организаций, а набор другой организации бэкенд привязать не даст (IMGM-1489).
   */
  organizerId: number;
  /** Настройки медиа соревнования — их читает и страница, чтобы выбрать подвкладку по умолчанию. */
  settings: ChampionshipMediaSettings | undefined;
  isFetching: boolean;
}

/** Бэкенд отказал в привязке: набор принадлежит другой организации (`AssignChampionshipTemplateGroupHandler`). */
const FOREIGN_GROUP_ERROR = 'not owned by this organization';

/**
 * Бэкенд отказал в генерации: у организации турнира нет тарифа с медиа-менеджером
 * («Media manager feature is not available» в ошибке `generateMedia`, задание не создаётся) (IMGM-1520).
 */
const MEDIA_FEATURE_UNAVAILABLE_ERROR = 'feature is not available';

/** Текст первой GraphQL-ошибки — его кладёт `graphqlBaseQuery` (`shared/api/base-api.ts`). */
function errorMessage(error: unknown): string | null {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return null;
}

/**
 * Подвкладка «Шаблоны» вкладки «Медиа»: набор шаблонов соревнования и типы медиа с кнопкой
 * «Сгенерировать». Готовая картинка появляется на соседней подвкладке «Картинки».
 */
const ChampionshipMediaTemplates = ({ championshipId, organizerId, settings, isFetching }: ChampionshipMediaTemplatesProps) => {
  const { t } = useLingui();
  const { data: groups = [], isLoading: isGroupsLoading } = useMediaManagerTemplateGroupsQuery(
    { tournamentId: organizerId },
    { skip: !organizerId },
  );
  const navigate = useNavigate();
  const [assignGroup] = useAssignChampionshipTemplateGroupMutation();
  const [unassignGroup, { isLoading: isUnassigning }] = useUnassignChampionshipTemplateGroupMutation();
  const [generateMedia] = useGenerateMediaMutation();
  /** Открыто окно «Сменить набор шаблонов»; карточка набора под ним остаётся на месте (IMGM-1512). */
  const [isChanging, setIsChanging] = useState(false);
  const [picker, setPicker] = useState<{ kind: MediaKind; subjectType: MediaSubjectType; title: string } | null>(null);
  /** Тип, чья генерация сейчас ставится в очередь: крутится только его кнопка, а не все сразу. */
  const [generatingKind, setGeneratingKind] = useState<MediaKind | null>(null);
  /** Набор, который сейчас назначается: крутится его кнопка, остальные ждут. */
  const [assigningGroupId, setAssigningGroupId] = useState<number | null>(null);

  // Ответ и так по одной организации; фильтр — страховка от глобальных наборов в выдаче.
  const organizerGroups = useMemo(() => groups.filter((group) => group.tournamentId === organizerId), [groups, organizerId]);

  const assigned = settings?.templateGroup;
  // Только подписка, результат читает окно «Сгенерировать». Один ответ на все типы объектов, поэтому
  // загруженный заранее он открывает окно сразу со списком: скелетон не знает числа объектов, и окно
  // меняло высоту на первом открытии (IMGM-1514). Без набора кнопок генерации нет — и запроса тоже.
  useChampionshipMediaSubjectsQuery(championshipId, { skip: !assigned });
  /** Выбор строками на странице — только пока набор не назначен; смена назначенного — в окне. */
  const showPicker = !assigned;
  /**
   * Пустой список наборов, пока они летят, — ещё не «у организации нет наборов»: по нему рисовалась
   * карточка «Создать набор», которая через миг сменялась списком (IMGM-1491). Только `isLoading`:
   * фоновый перезапрос уже показанный список не прячет (`.claude/docs/async-loading-states.md`).
   */
  const skeletonVariant = isFetching && !settings ? 'assigned' : showPicker && isGroupsLoading ? 'picker' : null;

  /** Назначает набор; `true` — успех. Тост об ошибке — здесь, на месте вызова, и для строк, и для окна. */
  const handleAssign = async (templateGroupId: number): Promise<boolean> => {
    if (assigningGroupId !== null) return false;
    setAssigningGroupId(templateGroupId);
    try {
      // Ответ мутации уже лежит в кэше настроек к возврату `unwrap()` — старый набор не мелькает (IMGM-1513).
      await assignGroup({ championshipId, templateGroupId }).unwrap();
      return true;
    } catch (error) {
      console.error('Failed to assign media template group:', error);
      const message = errorMessage(error);
      if (message?.includes(FOREIGN_GROUP_ERROR)) {
        toast.error(t`Не удалось привязать набор шаблонов: он принадлежит другой организации`);
      } else if (message) {
        toast.error(t`Не удалось привязать набор шаблонов: ${message}`);
      } else {
        toast.error(t`Не удалось привязать набор шаблонов`);
      }
      return false;
    } finally {
      setAssigningGroupId(null);
    }
  };

  const enqueueGenerate = async (kind: MediaKind, subjectId: number) => {
    setGeneratingKind(kind);
    try {
      await generateMedia({ championshipId, kind, subjectId }).unwrap();
      // Ведём на «Картинки» этого турнира, а не в «Очередь медиа»: очередь только для супер-админа.
      toast.success(
        <span>
          {t`Генерация запущена.`}{' '}
          <Link
            to={Pages.CHAMPIONSHIPS.MEDIA.builders.tab(championshipId, ChampionshipMediaTab.IMAGES)}
            className="font-semibold underline"
            data-testid="generated-toast-link"
          >
            {t`Посмотреть`}
          </Link>
        </span>,
      );
    } catch (error) {
      console.error('Failed to generate media:', error);
      if (errorMessage(error)?.includes(MEDIA_FEATURE_UNAVAILABLE_ERROR)) {
        toast.error(t`Не удалось запустить генерацию: у организации нет тарифа с медиа-менеджером`);
      } else {
        toast.error(t`Не удалось поставить генерацию в очередь`);
      }
    } finally {
      setGeneratingKind((current) => (current === kind ? null : current));
    }
  };

  const handleGenerate = (row: ChampionshipMediaKindRow) => {
    const subjectType = (row.subjectType as MediaSubjectType) || subjectTypeFor(row.kind);
    if (subjectType === 'championship') {
      void enqueueGenerate(row.kind, championshipId);
      return;
    }
    setPicker({ kind: row.kind, subjectType, title: row.title });
  };

  const handlePickerSubmit = (subjectId: number) => {
    if (!picker) return;
    const kind = picker.kind;
    setPicker(null);
    void enqueueGenerate(kind, subjectId);
  };

  const handleUnassign = async () => {
    try {
      await unassignGroup(championshipId).unwrap();
    } catch (error) {
      console.error('Failed to unassign media template group:', error);
      toast.error(t`Не удалось отвязать набор шаблонов`);
    }
  };

  const kindSubtitle = (row: ChampionshipMediaKindRow) => {
    switch (mediaKindState(row)) {
      case 'ready': {
        const templateName = row.templateName;
        return t`Шаблон «${templateName}»`;
      }
      case 'missing':
        return t`Добавьте в набор шаблон этого типа`;
      default:
        return t`Генерация этого типа появится позже`;
    }
  };

  const renderSkeleton = (variant: ChampionshipMediaTemplatesSkeletonVariant) => (
    <div className={MEDIA_TEMPLATES_COLUMN_CLASS} data-testid="championship-media-templates">
      <ChampionshipMediaTemplatesSkeleton variant={variant} />
    </div>
  );

  if (skeletonVariant) {
    return renderSkeleton(skeletonVariant);
  }

  return (
    // Сверка со скелетоном того же состояния экрана — только при `npm start` (IMGM-1491).
    <SkeletonPreview skeleton={renderSkeleton(showPicker ? 'picker' : 'assigned')}>
      <div className={MEDIA_TEMPLATES_COLUMN_CLASS} data-testid="championship-media-templates">
        {assigned ? (
          <section className={MEDIA_ASSIGNED_CARD_CLASS} data-testid="assigned-group-card">
            <div className={`${MEDIA_ASSIGNED_ICON_CLASS} bg-surfaceSunken text-inkSecondary`}>
              <FolderOpen size={22} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <UDText type="footnote" className="text-inkSecondary">
                {t(MEDIA_TEMPLATES_COPY.assignedLabel)}
              </UDText>
              <UDText type="subhead" className="text-inkPrimary truncate font-semibold">
                {assigned.name}
              </UDText>
              <UDText type="footnote" className="text-inkMuted">
                {plural(assigned.templatesCount, { one: '# шаблон', few: '# шаблона', many: '# шаблонов', other: '# шаблонов' })}
              </UDText>
            </div>
            <div className="flex shrink-0 gap-3">
              <UDButton variant="secondary" type="button" onClick={() => setIsChanging(true)} disabled={isUnassigning}>
                {t(MEDIA_TEMPLATES_COPY.change)}
              </UDButton>
              <UDButton variant="secondary" type="button" onClick={() => void handleUnassign()} loading={isUnassigning}>
                {t(MEDIA_TEMPLATES_COPY.unassign)}
              </UDButton>
            </div>
          </section>
        ) : null}

        {showPicker ? (
          <div className={MEDIA_SECTION_CLASS} data-testid="championship-media-picker">
            {organizerGroups.length === 0 ? (
              <MediaTabStateCard
                data-testid="championship-media-no-groups"
                icon={<Images size={26} />}
                title={t`У организации пока нет наборов шаблонов`}
                subtitle={t`Набор шаблонов — это группа шаблонов из медиа-менеджера, оформление турнира: по нему автоматически генерируются расписания, результаты и таблицы.`}
                action={
                  <div className="flex flex-wrap justify-center gap-3">
                    <UDButton variant="primary" type="button" onClick={() => navigate(Pages.MEDIA_MANAGER.INDEX)}>
                      <Trans>Создать набор</Trans>
                    </UDButton>
                    <UDButton variant="secondary" type="button" onClick={() => navigate(Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY)}>
                      <Trans>Взять из общих шаблонов</Trans>
                    </UDButton>
                  </div>
                }
              />
            ) : (
              <>
                <MediaTabStateCard
                  icon={<Images size={26} />}
                  title={t(MEDIA_TEMPLATES_COPY.pickTitle)}
                  subtitle={t(MEDIA_TEMPLATES_COPY.pickSubtitle)}
                />
                <div className={MEDIA_ROW_LIST_CLASS} role="list">
                  {organizerGroups.map((group) => (
                    <UDEntityRow
                      key={group.id}
                      data-testid={`media-group-row-${group.id}`}
                      title={group.name}
                      subtitle={plural(group.templatesCount, {
                        one: '# шаблон',
                        few: '# шаблона',
                        many: '# шаблонов',
                        other: '# шаблонов',
                      })}
                      onClick={() => void handleAssign(group.id)}
                      actions={
                        <UDButton
                          variant="secondary"
                          type="button"
                          onClick={() => handleAssign(group.id)}
                          loading={assigningGroupId === group.id}
                          disabled={assigningGroupId !== null && assigningGroupId !== group.id}
                          data-testid={`assign-group-${group.id}`}
                        >
                          {t(MEDIA_TEMPLATES_COPY.assign)}
                        </UDButton>
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        {settings && assigned ? (
          <section className={MEDIA_SECTION_CLASS} data-testid="championship-media-kinds">
            <div>
              <UDText type="title-3">{t(MEDIA_TEMPLATES_COPY.kindsTitle)}</UDText>
              <UDText type="subhead" className="text-inkSecondary">
                {t(MEDIA_TEMPLATES_COPY.kindsSubtitle)}
              </UDText>
            </div>
            <div className={MEDIA_ROW_LIST_CLASS} role="list">
              {settings.kinds.map((row) => {
                const state = mediaKindState(row);
                return (
                  <UDEntityRow
                    key={row.kind}
                    data-testid={`media-kind-row-${row.kind}`}
                    title={row.title}
                    subtitle={kindSubtitle(row)}
                    actions={
                      <>
                        <MediaKindStatus state={state} />
                        {/* Обе кнопки одной ширины, различаются стилем: генерация — главное действие вкладки (IMGM-1518). */}
                        {state === 'ready' ? (
                          <UDButton
                            variant="primary"
                            type="button"
                            className={MEDIA_KIND_ACTION_CLASS}
                            onClick={() => handleGenerate(row)}
                            loading={generatingKind === row.kind}
                            data-testid={`generate-media-${row.kind}`}
                          >
                            {t(MEDIA_TEMPLATES_COPY.generate)}
                          </UDButton>
                        ) : null}
                        {/* Ведёт в назначенный набор с открытым окном и подставленным типом (IMGM-1518). */}
                        {state === 'missing' ? (
                          <UDButton
                            variant="secondary"
                            type="button"
                            className={MEDIA_KIND_ACTION_CLASS}
                            onClick={() => navigate(Pages.MEDIA_MANAGER.builders.group(assigned.id, { create: row.kind }))}
                            data-testid={`create-template-${row.kind}`}
                          >
                            {t(MEDIA_TEMPLATES_COPY.createTemplate)}
                          </UDButton>
                        ) : null}
                        {/* У «Скоро» действия нет — место держится, чтобы статусы всех строк стояли одной колонкой. */}
                        {state === 'soon' ? <span className={`${MEDIA_KIND_ACTION_CLASS} shrink-0`} aria-hidden /> : null}
                      </>
                    }
                  />
                );
              })}
            </div>
          </section>
        ) : null}

        {/* Монтируется только на время показа: закрытие сбрасывает выбор в окне. */}
        {isChanging && assigned ? (
          <ChangeTemplateGroupModal
            groups={organizerGroups}
            isLoading={isGroupsLoading}
            currentGroupId={assigned.id}
            onAssign={handleAssign}
            onClose={() => setIsChanging(false)}
          />
        ) : null}

        {picker ? (
          <GenerateMediaModal
            championshipId={championshipId}
            kind={picker.kind}
            subjectType={picker.subjectType}
            kindTitle={picker.title}
            onClose={() => setPicker(null)}
            onSubmit={handlePickerSubmit}
          />
        ) : null}
      </div>
    </SkeletonPreview>
  );
};

export default ChampionshipMediaTemplates;
